// Spelmotor: de stand wordt altijd afgeleid uit de lijst met invoer (darts, beurttotalen, volgende leg).
// Ongedaan maken is daardoor simpelweg het laatste item weghalen; alle statistieken rekenen vanzelf mee.
import {
  CricketPlayerState,
  DartInput,
  GameConfig,
  GameState,
  MatchHistoryRecord,
  OutMode,
  Player,
  TurnHistory
} from '../types/darts';

export type MatchEvent =
  | { type: 'dart'; dart: DartInput; at: number }
  // Beurttotaal (X01 en Count-Up). darts: aantal gebruikte darts; dartsAtDouble: darts op een uitgooi-vak.
  | { type: 'total'; total: number; darts?: number; dartsAtDouble?: number; at: number }
  | { type: 'next-leg'; at: number };

export interface Match {
  id: string;
  config: GameConfig;
  players: Player[];
  events: MatchEvent[];
  startedAt: number;
}

export const CRICKET_TARGETS = [15, 16, 17, 18, 19, 20, 25];

// Scores die je met double-out niet in één beurt kunt uitgooien.
const DOUBLE_OUT_BOGEYS = [169, 168, 166, 165, 163, 162, 159];

export function createMatch(config: GameConfig, players: Player[], now = Date.now()): Match {
  return { id: `match-${now}`, config, players, events: [], startedAt: now };
}

export function isX01(config: GameConfig): boolean {
  return config.gameMode !== 'cricket' && config.gameMode !== 'countup';
}

/** Kan deze score met één dart worden uitgegooid? Dan telt die dart als uitgooi-poging. */
export function isOneDartFinish(score: number, outMode: OutMode): boolean {
  const onDouble = score === 50 || (score >= 2 && score <= 40 && score % 2 === 0);
  const onTriple = score >= 3 && score <= 60 && score % 3 === 0;
  if (outMode === 'double') return onDouble;
  if (outMode === 'master') return onDouble || onTriple;
  return onDouble || onTriple || (score >= 1 && score <= 20) || score === 25;
}

/** Kan deze resterende score in één beurt (max. 3 darts) worden uitgegooid? */
export function canFinishInOneTurn(score: number, outMode: OutMode): boolean {
  if (score <= 0) return false;
  if (outMode === 'double') return score <= 170 && !DOUBLE_OUT_BOGEYS.includes(score);
  return minDartsToFinish(score, outMode) !== null;
}

const SINGLE_SCORES = [...Array.from({ length: 20 }, (_, i) => i + 1), 25];
const ALL_DART_SCORES = [
  ...SINGLE_SCORES,
  ...SINGLE_SCORES.map(v => v * 2),
  ...Array.from({ length: 20 }, (_, i) => (i + 1) * 3)
];

/** Minimaal aantal darts om deze score uit te gooien (1–3), of null als het niet in één beurt kan. */
export function minDartsToFinish(score: number, outMode: OutMode): number | null {
  if (isOneDartFinish(score, outMode)) return 1;
  for (const first of ALL_DART_SCORES) {
    if (isOneDartFinish(score - first, outMode)) return 2;
  }
  for (const first of ALL_DART_SCORES) {
    for (const second of ALL_DART_SCORES) {
      if (isOneDartFinish(score - first - second, outMode)) return 3;
    }
  }
  return null;
}

function isBustAfterDart(remaining: number, dart: DartInput, outMode: OutMode): boolean {
  if (remaining < 0) return true;
  if (outMode === 'single') return false;
  if (remaining === 1) return true;
  if (remaining === 0) {
    if (outMode === 'double') return dart.multiplier !== 2;
    return dart.multiplier === 1; // master: dubbel of triple
  }
  return false;
}

/** Verdeelt een beurttotaal in hele punten over de darts (de rest op de laatste dart), zonder afrondingsfouten. */
function spread(total: number, darts: number): number[] {
  const base = Math.floor(total / darts);
  return Array.from({ length: darts }, (_, i) => (i === darts - 1 ? total - base * (darts - 1) : base));
}

function emptyCricket(playerId: string): CricketPlayerState {
  return { playerId, hits: { 15: 0, 16: 0, 17: 0, 18: 0, 19: 0, 20: 0, 25: 0 }, points: 0 };
}

function perPlayer<T>(players: Player[], make: (p: Player) => T): Record<string, T> {
  const out: Record<string, T> = {};
  players.forEach(p => { out[p.id] = make(p); });
  return out;
}

/** Bouwt de volledige stand op uit de invoerlijst. */
export function deriveState(match: Match): GameState {
  const { config, players } = match;
  const n = players.length;
  const startScore = config.gameMode === 'countup' ? 0 : config.startingScore;
  const zero = () => perPlayer(players, () => 0);

  const s: GameState = {
    config,
    players,
    activePlayerIndex: 0,
    starterPlayerIndex: 0,
    scores: perPlayer(players, () => startScore),
    legsWon: zero(),
    setsWon: zero(),
    currentTurnDarts: [],
    turnHistory: [],
    cricketState: perPlayer(players, p => emptyCricket(p.id)),
    dartsThrown: zero(),
    totalPointsScored: zero(),
    highestTurn: zero(),
    highestCheckout: zero(),
    checkoutAttempts: zero(),
    checkoutHits: zero(),
    count180s: zero(),
    count140Plus: zero(),
    count100Plus: zero(),
    first9Points: zero(),
    first9Darts: zero(),
    cricketMarks: zero(),
    isLegOver: false,
    isMatchOver: false,
    winnerId: null,
    legWinnerId: null,
    legNumber: 1,
    startedAt: match.startedAt
  };

  // Darts per speler in de huidige leg (voor het gemiddelde over de eerste 9 darts).
  let legDarts = zero();
  let turnStartCricket: CricketPlayerState | null = null;

  const addFirst9 = (playerId: string, dartScores: number[]) => {
    dartScores.forEach(score => {
      if (legDarts[playerId] < 9) {
        s.first9Points[playerId] += score;
        s.first9Darts[playerId] += 1;
      }
      legDarts[playerId] += 1;
    });
  };

  const winLeg = (playerId: string) => {
    s.legsWon[playerId] += 1;
    s.isLegOver = true;
    s.legWinnerId = playerId;
    if (s.legsWon[playerId] >= config.firstToLegs) {
      s.isMatchOver = true;
      s.winnerId = playerId;
    }
  };

  const endTurn = (
    playerId: string,
    darts: DartInput[],
    turnTotal: number,
    scoreBefore: number,
    scoreAfter: number,
    isBust: boolean,
    dartCount: number,
    at: number,
    isCheckout: boolean
  ) => {
    const player = players.find(p => p.id === playerId)!;
    const points = isBust ? 0 : turnTotal;
    s.dartsThrown[playerId] += dartCount;
    s.totalPointsScored[playerId] += points;
    if (config.gameMode !== 'cricket') {
      s.highestTurn[playerId] = Math.max(s.highestTurn[playerId], points);
      if (points === 180) s.count180s[playerId] += 1;
      else if (points >= 140) s.count140Plus[playerId] += 1;
      else if (points >= 100) s.count100Plus[playerId] += 1;
    }
    if (isCheckout) {
      s.checkoutHits[playerId] += 1;
      s.highestCheckout[playerId] = Math.max(s.highestCheckout[playerId], turnTotal);
    }
    s.turnHistory.push({
      id: `turn-${s.turnHistory.length + 1}`,
      playerId,
      playerName: player.name,
      darts,
      turnTotal: points,
      attemptedTotal: turnTotal,
      scoreBefore,
      scoreAfter,
      isBust,
      timestamp: at,
      gameMode: config.gameMode,
      legNumber: s.legNumber,
      dartCount
    });
    s.currentTurnDarts = [];
    turnStartCricket = null;
    if (!s.isLegOver) s.activePlayerIndex = (s.activePlayerIndex + 1) % n;
  };

  for (const ev of match.events) {
    if (s.isMatchOver) break;

    if (ev.type === 'next-leg') {
      if (!s.isLegOver) continue;
      s.isLegOver = false;
      s.legWinnerId = null;
      s.legNumber += 1;
      s.starterPlayerIndex = (s.starterPlayerIndex + 1) % n;
      s.activePlayerIndex = s.starterPlayerIndex;
      s.scores = perPlayer(players, () => startScore);
      s.cricketState = perPlayer(players, p => emptyCricket(p.id));
      s.currentTurnDarts = [];
      legDarts = zero();
      continue;
    }
    if (s.isLegOver) continue;

    const player = players[s.activePlayerIndex];
    const pid = player.id;

    if (ev.type === 'total') {
      if (config.gameMode === 'cricket' || s.currentTurnDarts.length > 0) continue;
      const total = ev.total;
      const dartCount = Math.min(3, Math.max(1, ev.darts ?? 3));
      const before = s.scores[pid];

      if (config.gameMode === 'countup') {
        const after = before + total;
        s.scores[pid] = after;
        addFirst9(pid, spread(total, dartCount));
        const won = after >= config.startingScore;
        if (won) winLeg(pid);
        endTurn(pid, [], total, before, after, false, dartCount, ev.at, false);
        continue;
      }

      const remaining = before - total;
      const isFinish = remaining === 0 && canFinishInOneTurn(before, config.outMode);
      const isBust = remaining < 0 || (remaining === 0 && !isFinish) || (remaining === 1 && config.outMode !== 'single');
      s.checkoutAttempts[pid] += ev.dartsAtDouble ?? (isFinish ? 1 : 0);
      addFirst9(pid, spread(isBust ? 0 : total, dartCount));
      if (!isBust) s.scores[pid] = remaining;
      if (isFinish) winLeg(pid);
      endTurn(pid, [], total, before, isBust ? before : remaining, isBust, dartCount, ev.at, isFinish);
      continue;
    }

    // Losse dart
    const dart = ev.dart;
    const turnDarts = [...s.currentTurnDarts, dart];
    const turnSum = turnDarts.reduce((acc, d) => acc + d.score, 0);

    if (config.gameMode === 'countup') {
      const before = s.scores[pid];
      const after = before + turnSum;
      addFirst9(pid, [dart.score]);
      if (after >= config.startingScore) {
        s.scores[pid] = after;
        winLeg(pid);
        endTurn(pid, turnDarts, turnSum, before, after, false, turnDarts.length, ev.at, false);
      } else if (turnDarts.length === 3) {
        s.scores[pid] = after;
        endTurn(pid, turnDarts, turnSum, before, after, false, 3, ev.at, false);
      } else {
        s.currentTurnDarts = turnDarts;
      }
      continue;
    }

    if (config.gameMode === 'cricket') {
      if (!turnStartCricket) turnStartCricket = s.cricketState[pid];
      const cs = s.cricketState[pid];
      const hits = { ...cs.hits };
      let points = 0;
      if (CRICKET_TARGETS.includes(dart.value)) {
        const current = hits[dart.value] || 0;
        const total = current + dart.multiplier;
        hits[dart.value] = total;
        s.cricketMarks[pid] += dart.multiplier;
        if (total > 3) {
          const overflow = current >= 3 ? dart.multiplier : total - 3;
          const allOpponentsClosed = players
            .filter(p => p.id !== pid)
            .every(p => (s.cricketState[p.id].hits[dart.value] || 0) >= 3);
          if (!allOpponentsClosed) points = overflow * dart.value;
        }
      }
      s.cricketState = { ...s.cricketState, [pid]: { ...cs, hits, points: cs.points + points } };

      const myPoints = cs.points + points;
      const closedAll = CRICKET_TARGETS.every(t => (hits[t] || 0) >= 3);
      const bestOther = Math.max(0, ...players.filter(p => p.id !== pid).map(p => s.cricketState[p.id].points));
      const before = turnStartCricket.points;
      if (closedAll && myPoints >= bestOther) {
        winLeg(pid);
        endTurn(pid, turnDarts, myPoints - before, before, myPoints, false, turnDarts.length, ev.at, false);
      } else if (turnDarts.length === 3) {
        endTurn(pid, turnDarts, myPoints - before, before, myPoints, false, 3, ev.at, false);
      } else {
        s.currentTurnDarts = turnDarts;
      }
      continue;
    }

    // X01
    const turnStart = s.scores[pid];
    const before = turnStart - (turnSum - dart.score);
    const remaining = before - dart.score;
    if (isOneDartFinish(before, config.outMode)) s.checkoutAttempts[pid] += 1;
    addFirst9(pid, [dart.score]);

    if (isBustAfterDart(remaining, dart, config.outMode)) {
      endTurn(pid, turnDarts, turnSum, turnStart, turnStart, true, turnDarts.length, ev.at, false);
    } else if (remaining === 0) {
      s.scores[pid] = 0;
      winLeg(pid);
      endTurn(pid, turnDarts, turnSum, turnStart, 0, false, turnDarts.length, ev.at, true);
    } else if (turnDarts.length === 3) {
      s.scores[pid] = remaining;
      endTurn(pid, turnDarts, turnSum, turnStart, remaining, false, 3, ev.at, false);
    } else {
      s.currentTurnDarts = turnDarts;
    }
  }

  return s;
}

function append(match: Match, ev: MatchEvent): Match {
  return { ...match, events: [...match.events, ev] };
}

export function addDart(match: Match, dart: DartInput, at = Date.now()): Match {
  return append(match, { type: 'dart', dart, at });
}

export function addTurnTotal(
  match: Match,
  total: number,
  opts: { darts?: number; dartsAtDouble?: number } = {},
  at = Date.now()
): Match {
  return append(match, { type: 'total', total, ...opts, at });
}

export function startNextLeg(match: Match, at = Date.now()): Match {
  return append(match, { type: 'next-leg', at });
}

/** Haalt de laatste invoer weg. Staat er net een nieuwe leg klaar, dan gaat ook de winnende worp terug. */
export function undo(match: Match): Match {
  const events = [...match.events];
  while (events.length > 0 && events[events.length - 1].type === 'next-leg') events.pop();
  events.pop();
  return { ...match, events };
}

export function canUndo(match: Match): boolean {
  return match.events.some(e => e.type !== 'next-leg');
}

export function lastTurn(state: GameState): TurnHistory | undefined {
  return state.turnHistory[state.turnHistory.length - 1];
}

/** Maakt het geschiedenis-record van een afgelopen wedstrijd. */
export function toHistoryRecord(match: Match, state: GameState): MatchHistoryRecord {
  const winner = match.players.find(p => p.id === state.winnerId);
  const endedAt = match.events.length > 0 ? match.events[match.events.length - 1].at : match.startedAt;
  return {
    id: match.id,
    playedAt: endedAt,
    date: new Date(endedAt).toLocaleDateString('nl-NL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }),
    gameMode: match.config.gameMode,
    players: match.players.map(p => {
      const darts = state.dartsThrown[p.id];
      const points = state.totalPointsScored[p.id];
      const attempts = state.checkoutAttempts[p.id];
      const hits = state.checkoutHits[p.id];
      return {
        id: p.id,
        name: p.name,
        legsWon: state.legsWon[p.id],
        avg3Dart: darts > 0 ? Math.round((points / darts) * 30) / 10 : 0,
        highestTurn: state.highestTurn[p.id],
        highestCheckout: state.highestCheckout[p.id],
        checkoutAttempts: attempts,
        checkoutHits: hits,
        checkoutPercent: attempts > 0 ? Math.round((hits / attempts) * 100) : 0,
        count180s: state.count180s[p.id],
        count140Plus: state.count140Plus[p.id],
        count100Plus: state.count100Plus[p.id],
        dartsThrown: darts,
        pointsScored: points,
        first9Points: state.first9Points[p.id],
        first9Darts: state.first9Darts[p.id],
        isWinner: p.id === state.winnerId
      };
    }),
    winnerName: winner?.name ?? '',
    durationSeconds: Math.round((endedAt - match.startedAt) / 1000),
    totalTurns: state.turnHistory.length
  };
}

/** Controleert een opgeslagen wedstrijd; bij twijfel liever geen wedstrijd dan een crash. */
export function parseMatch(value: unknown): Match | null {
  if (!value || typeof value !== 'object') return null;
  const m = value as Partial<Match>;
  if (typeof m.id !== 'string' || typeof m.startedAt !== 'number') return null;
  if (!m.config || typeof m.config !== 'object' || typeof m.config.gameMode !== 'string') return null;
  if (!Array.isArray(m.players) || m.players.length === 0) return null;
  if (!m.players.every(p => p && typeof p.id === 'string' && typeof p.name === 'string')) return null;
  if (!Array.isArray(m.events)) return null;
  const okEvent = (e: unknown) => {
    if (!e || typeof e !== 'object') return false;
    const ev = e as MatchEvent;
    if (typeof ev.at !== 'number') return false;
    if (ev.type === 'next-leg') return true;
    if (ev.type === 'total') return typeof ev.total === 'number' && ev.total >= 0 && ev.total <= 180;
    if (ev.type === 'dart') {
      const d = ev.dart;
      return !!d && [1, 2, 3].includes(d.multiplier) && typeof d.value === 'number' && typeof d.score === 'number';
    }
    return false;
  };
  if (!m.events.every(okEvent)) return null;
  try {
    deriveState(m as Match);
  } catch {
    return null;
  }
  return m as Match;
}
